import { ActionIcon, Badge, Box, Group, Paper, SimpleGrid, Stack, Text, Title, Tooltip } from '@mantine/core';
import { IconEdit, IconAdjustmentsHorizontal, IconSettings, IconLayoutGrid } from '@tabler/icons-react';
import type { OccupancyView, Zone } from '../api-client.ts';
import type { ZoneWithOccupancy } from '../hooks/useDashboard.ts';
import { tokens } from '../ui/tokens.ts';
import {
  formatZoneCapacityRange,
  formatZonePricing,
  formatPartySizeRange,
} from '../ui/formatters.ts';

interface Segment {
  readonly startPosition: number;
  readonly endPosition: number;
}

export interface SeatMapProps {
  readonly zones: readonly ZoneWithOccupancy[];
  readonly loading?: boolean;
  readonly unitLabel?: string;
  readonly highlight?: Segment | null;
  readonly focusResourceId?: string;
  /** When provided, each zone header renders an edit button wired to this handler. */
  readonly onEditPricing?: (zone: Zone) => void;
  /** When provided, each zone header renders a '編輯規則' button. */
  readonly onEditPolicy?: (zone: Zone) => void;
  /** When provided, each zone header renders a '區域設定' button. */
  readonly onEditMetadata?: (zone: Zone) => void;
  /** When provided, each zone header renders a 'unit 管理' button. */
  readonly onEditUnits?: (zone: Zone) => void;
}

export function SeatMap(props: SeatMapProps) {
  const { zones, loading, unitLabel = '格', highlight, focusResourceId, onEditPricing, onEditPolicy, onEditMetadata, onEditUnits } = props;

  if (loading) {
    return (
      <Paper withBorder p="md" radius="lg">
        <Text size="sm" c="dimmed">
          載入中…
        </Text>
      </Paper>
    );
  }

  if (zones.length === 0) {
    return (
      <Paper withBorder p="md" radius="lg">
        <Text size="sm" c="dimmed">
          尚未設定 zone — 請執行{' '}
          <Text span ff="monospace">
            bash scripts/demo/walkthrough_play.sh
          </Text>
          。
        </Text>
      </Paper>
    );
  }

  const visible = focusResourceId
    ? zones.filter((z) => z.zone.resourceId === focusResourceId)
    : zones;

  return (
    <Stack gap="lg">
      {visible.map((zoneState) => (
        <ZonePanel
          key={zoneState.zone.resourceId}
          zoneState={zoneState}
          unitLabel={unitLabel}
          highlight={highlight ?? null}
          {...(onEditPricing ? { onEditPricing } : {})}
          {...(onEditPolicy ? { onEditPolicy } : {})}
          {...(onEditMetadata ? { onEditMetadata } : {})}
          {...(onEditUnits ? { onEditUnits } : {})}
        />
      ))}
    </Stack>
  );
}

interface ZonePanelProps {
  readonly zoneState: ZoneWithOccupancy;
  readonly unitLabel: string;
  readonly highlight: Segment | null;
  readonly onEditPricing?: (zone: Zone) => void;
  readonly onEditPolicy?: (zone: Zone) => void;
  readonly onEditMetadata?: (zone: Zone) => void;
  readonly onEditUnits?: (zone: Zone) => void;
}

function ZonePanel({ zoneState, unitLabel, highlight, onEditPricing, onEditPolicy, onEditMetadata, onEditUnits }: ZonePanelProps) {
  const { zone, occupancy } = zoneState;
  return (
    <Paper withBorder p="lg" radius="xl" shadow="sm">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={5}>{zone.zoneLabel}</Title>
            <Text size="xs" c="dimmed">
              {formatZoneCapacityRange(zone, unitLabel)} · {formatPartySizeRange(zone)}
            </Text>
          </Stack>
          <Group gap="xs" align="center">
            <Badge color={tokens.color.mantine.primary} variant="filled" radius="sm">
              {formatZonePricing(zone)}
            </Badge>
            {onEditPricing && (
              <Tooltip label="編輯價格">
                <ActionIcon
                  variant="subtle"
                  color={tokens.color.mantine.primary}
                  onClick={() => onEditPricing(zone)}
                  aria-label="編輯價格"
                  data-testid={`zone-edit-pricing-${zone.resourceId}`}
                >
                  <IconEdit size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {onEditPolicy && (
              <Tooltip label="編輯規則（人數 / 時長 / 禁用時段）">
                <ActionIcon
                  variant="subtle"
                  color={tokens.color.mantine.primary}
                  onClick={() => onEditPolicy(zone)}
                  aria-label="編輯規則"
                  data-testid={`zone-edit-policy-${zone.resourceId}`}
                >
                  <IconAdjustmentsHorizontal size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {onEditMetadata && (
              <Tooltip label="區域設定（名稱 / 容量 / 啟用）">
                <ActionIcon
                  variant="subtle"
                  color={tokens.color.mantine.primary}
                  onClick={() => onEditMetadata(zone)}
                  aria-label="區域設定"
                  data-testid={`zone-edit-metadata-${zone.resourceId}`}
                >
                  <IconSettings size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {onEditUnits && (
              <Tooltip label={`${unitLabel}管理（個別${unitLabel}的名稱 / 容量 / 啟用）`}>
                <ActionIcon
                  variant="subtle"
                  color={tokens.color.mantine.primary}
                  onClick={() => onEditUnits(zone)}
                  aria-label={`${unitLabel}管理`}
                  data-testid={`zone-edit-units-${zone.resourceId}`}
                >
                  <IconLayoutGrid size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Group>
        {zone.allocationMode === 'exclusive_zone' ? (
          <ExclusiveZoneTile zone={zone} occupancy={occupancy} />
        ) : (
          <ZoneGrid occupancy={occupancy} highlight={highlight} />
        )}
        <Group gap="lg" mt="xs">
          {zone.allocationMode === 'exclusive_zone' ? (
            <>
              <Legend swatch={tokens.tile.occupied.background} label="包場中" />
              <Legend swatch={tokens.tile.idle.background} label="空閒" />
            </>
          ) : (
            <>
              <Legend swatch={tokens.tile.occupied.background} label={`使用中的${unitLabel}`} />
              <Legend swatch={tokens.tile.buffer.background} label="緩衝" />
              <Legend swatch={tokens.tile.idle.background} label="空閒" />
              {highlight && <Legend swatch={tokens.tile.highlight.background} label="候選段" />}
            </>
          )}
        </Group>
      </Stack>
    </Paper>
  );
}

function ExclusiveZoneTile({
  zone,
  occupancy,
}: {
  readonly zone: Zone;
  readonly occupancy: OccupancyView | null;
}) {
  const occupiedSlots = occupancy?.slots.filter((s) => !!s.occupiedByReservationId) ?? [];
  const isBooked = occupiedSlots.length > 0;
  const state = isBooked ? tokens.tile.occupied : tokens.tile.idle;
  return (
    <Box
      style={{
        background: state.background,
        borderRadius: tokens.radius.xl,
        padding: '28px 24px',
        textAlign: 'center',
        minHeight: 120,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        boxShadow: state.shadow,
        border: `1px solid ${tokens.color.cardBorder}`,
      }}
      data-testid={`exclusive-zone-tile-${zone.resourceId}`}
    >
      <Text fw={800} style={{ color: state.textColor, fontSize: 22 }}>
        {isBooked ? '已包場' : '空閒 · 可預訂包場'}
      </Text>
      <Text size="sm" style={{ color: state.textColor, opacity: 0.9 }}>
        {isBooked ? '此時段整個區域已被一組客人包場' : `最多容納 ${zone.maxPartySize} 位 · 整場獨占`}
      </Text>
    </Box>
  );
}

function ZoneGrid({
  occupancy,
  highlight,
}: {
  readonly occupancy: OccupancyView | null;
  readonly highlight: Segment | null;
}) {
  if (!occupancy) {
    return (
      <Text size="sm" c="dimmed">
        無占用資料
      </Text>
    );
  }
  return (
    <SimpleGrid cols={{ base: 4, sm: 4, md: 4 }} spacing="sm">
      {occupancy.slots.map((slot) => {
        const state = tokens.tile.forSlot({
          active: slot.active,
          occupied: !!slot.occupiedByReservationId,
          isBuffer: slot.isBuffer,
          highlighted:
            !!highlight &&
            slot.position >= highlight.startPosition &&
            slot.position <= highlight.endPosition,
        });
        return (
          <Box
            key={slot.position}
            style={{
              background: state.background,
              borderRadius: tokens.radius.lg,
              padding: '14px 0',
              textAlign: 'center',
              minHeight: 68,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              boxShadow: state.shadow,
              border: slot.active ? `1px solid ${tokens.color.cardBorder}` : 'none',
            }}
          >
            <Text fw={700} style={{ color: state.textColor, fontSize: 15 }}>
              {slot.label}
            </Text>
            <Text size="xs" style={{ color: state.textColor, opacity: 0.9 }}>
              {state.label}
            </Text>
          </Box>
        );
      })}
    </SimpleGrid>
  );
}

function Legend({ swatch, label }: { readonly swatch: string; readonly label: string }) {
  return (
    <Group gap={6}>
      <Box
        style={{
          width: 18,
          height: 18,
          borderRadius: 6,
          background: swatch,
          boxShadow: tokens.shadow.card,
          border: `1px solid ${tokens.color.cardBorder}`,
        }}
      />
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
}
